import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ApiError, api } from "@/lib/api";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { Button } from "@/components/ui/button";

type Plan = {
  id: number;
  code: string;
  name: string;
  products: string[];
  description?: string | null;
  active: boolean;
};

type Subscription = {
  id: number;
  account_id: number;
  plan_id: number;
  status: string;
  starts_at: string;
  ends_at: string;
  effective: boolean;
  products: string[];
};

export default function SubscriptionsPage() {
  const { accountId, accessKind, refreshModules } = useAccountWorkspace();
  const canManage = accessKind === "membership";
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busyPlanId, setBusyPlanId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [plansPayload, subsPayload] = await Promise.all([
        api.fetch<{ plans: Plan[] }>("/plans"),
        api.fetchForAccount<{ subscriptions: Subscription[] }>(
          accountId,
          `/accounts/${accountId}/subscriptions`,
        ),
      ]);
      setPlans(plansPayload.plans);
      setSubscriptions(subsPayload.subscriptions);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法加载订阅信息");
    } finally {
      setIsLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function subscribeToPlan(planId: number) {
    if (!canManage) return;
    setBusyPlanId(planId);
    try {
      const startsAt = new Date().toISOString();
      const endsAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      await api.fetchForAccount(accountId, `/accounts/${accountId}/subscriptions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_id: planId, starts_at: startsAt, ends_at: endsAt }),
      });
      toast.success("已开通订阅");
      await load();
      await refreshModules();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "开通失败");
    } finally {
      setBusyPlanId(null);
    }
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">正在加载订阅…</p>;
  }

  if (error) {
    return (
      <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
        {error}
      </p>
    );
  }

  return (
    <div className="space-y-8">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">订阅</h1>
        <p className="text-sm text-muted-foreground">
          查看当前账户套餐与开通状态。无支付渠道时可通过 Mock 开通（仅成员管理员）。
        </p>
      </div>

      <section className="space-y-3">
        <h2 className="font-medium">当前订阅</h2>
        {subscriptions.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无订阅记录。</p>
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {subscriptions.map((subscription) => {
              const plan = plans.find((item) => item.id === subscription.plan_id);
              return (
                <li key={subscription.id} className="flex flex-wrap items-start justify-between gap-3 px-5 py-4">
                  <div>
                    <div className="font-medium">{plan?.name ?? `套餐 #${subscription.plan_id}`}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      状态 {subscription.status}
                      {subscription.effective ? " · 有效" : " · 无效"} · products:{" "}
                      {subscription.products.join(", ") || "—"}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {new Date(subscription.starts_at).toLocaleDateString()} —{" "}
                      {new Date(subscription.ends_at).toLocaleDateString()}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-medium">可用套餐</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {plans.map((plan) => (
            <div key={plan.id} className="rounded-lg border border-border bg-card p-5">
              <div className="font-medium">{plan.name}</div>
              <p className="mt-1 text-sm text-muted-foreground">{plan.description || plan.code}</p>
              <p className="mt-2 text-xs text-muted-foreground">products: {plan.products.join(", ")}</p>
              {canManage ? (
                <Button
                  className="mt-4"
                  size="sm"
                  disabled={busyPlanId === plan.id}
                  onClick={() => void subscribeToPlan(plan.id)}
                >
                  {busyPlanId === plan.id ? "开通中…" : "Mock 开通 30 天"}
                </Button>
              ) : (
                <p className="mt-4 text-xs text-muted-foreground">代管只读，无法改订阅。</p>
              )}
            </div>
          ))}
          {plans.length === 0 ? <p className="text-sm text-muted-foreground">暂无可用套餐。</p> : null}
        </div>
      </section>
    </div>
  );
}
